import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { forwardRef, useImperativeHandle } from 'react'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../models'
import { events } from '../../models/analytics/analytics-events'
import { DS } from '../../design-system'
import { ChapterModelType } from '../../models/api-store'

interface CurrentVideoProps {
  isPlaying: boolean
  currentPosition?: number
  completed?: boolean
}
type VideoPlayerProps = {
  chapter: ChapterModelType
  currentVideo?: CurrentVideoProps
  setCurrentVideo: (params: CurrentVideoProps, _id: string | null | undefined) => void
  setDurationChapter: (chapterId: string, duration: number) => void
}

interface SubtitleTrack {
  id: string
  language: string
  title: string
}

interface VideoPlayerRef {
  resume: () => void
  pause: () => void
  seek: (time: number) => void
}

/**
 * Web-specific video player component that uses the HTML5 video element
 * with support for subtitles and fullscreen
 *
 * @param {VideoPlayerProps} props The properties for the video player
 */
export const VideoPlayer = observer(
  forwardRef<VideoPlayerRef, VideoPlayerProps>(
    (
      {
        chapter,
        // bufferConfig = DEFAULT_BUFFER_CONFIG,
        setCurrentVideo,
        setDurationChapter,
        ...rest
      }: VideoPlayerProps,
      ref
    ) => {
      const {
        lessons: { lessonSelected },
        analytics: { setEvent }
      } = useStore()

      const videoRef = useRef<HTMLVideoElement>(null)

      const [subtitles, setSubtitles] = useState<SubtitleTrack | null>(null)
      const [isPlaying, setIsPlaying] = useState(false)
      const [isFullScreen, setIsFullScreen] = useState(false)
      const [duration, setDuration] = useState(0)

      const areSubsDisabled = subtitles === null
      const areSubsAvailable = !!chapter.subtitlesUrl
      const textButton = areSubsDisabled ? 'Enable' : 'Disable'

      useImperativeHandle(ref, () => ({
        ...ref,
        resume: () => {
          videoRef.current?.play()
        },
        pause: () => {
          videoRef.current?.pause()
        },
        seek: (time: number) => {
          if (videoRef.current) {
            videoRef.current.currentTime = time
          }
        }
      }))

      /**
       * Toggle subtitles on/off using HTML5 track element
       * @returns {void} Toggles the subtitles on and off
       */
      const changeSubtitleStatus = useCallback(() => {
        if (videoRef.current) {
          const track = videoRef.current?.textTracks?.[0]

          if (!track) {
            return
          }

          if (areSubsDisabled) {
            // Enable subtitles

            track.mode = 'showing'
            setSubtitles({ id: 'en', language: 'en', title: 'English' })
          } else {
            // Disable subtitles

            track.mode = 'hidden'
            setSubtitles(null)
          }
        }
      }, [areSubsDisabled, subtitles, chapter.subtitlesUrl])

      const startFromLastPosition = useCallback(() => {
        if (videoRef.current && chapter.currentPosition) {
          videoRef.current.currentTime = chapter.currentPosition
        }
      }, [chapter.currentPosition])

      /**
       * Called when the video is loaded, use it to start the video from the last position played. Stores
       * the duration of the video in case it's needed.
       */
      // const onLoad = (data: OnLoadData) => {
      //   if (!chapter._id) return

      //   videoRef.current?.resume()
      //   setDurationChapter(chapter._id, data.duration)
      //   startFromLastPosition()
      // }

      /**
       * Called when the video ends, use it to mark the video as watched and jump to the next one
       * This function could be replaced and just send the prop directly to the parent component
       */
      const onEnd = useCallback(() => {
        setEvent(events.videoLessons.chapter_completed, {
          lesson_name: lessonSelected?.name,
          lesson_id: lessonSelected?.lessonId,
          chapter_name: chapter?.name,
          chapter_id: chapter?._id
        })

        setCurrentVideo({ currentPosition: duration, completed: true, isPlaying: false }, chapter._id)
      }, [
        setEvent,
        lessonSelected?.name,
        lessonSelected?.lessonId,
        chapter?.name,
        chapter?._id,
        duration,
        setCurrentVideo,
        chapter._id
      ])

      // Safely store chapter id to prevent MobX errors with a useMemo to prevent recreation
      const chapterId = useMemo(() => chapter?._id, [chapter?._id])

      // Define event handlers with useCallback to prevent unnecessary recreations
      const handleMetadata = useCallback(() => {
        if (videoRef.current) {
          setDuration(videoRef.current.duration)
          if (chapterId) {
            setDurationChapter(chapterId, videoRef.current.duration)
          }
          startFromLastPosition()
          // Start with paused state
          videoRef.current.pause()
        }
      }, [chapterId, setDurationChapter, startFromLastPosition])

      const handlePlay = useCallback(() => {
        setIsPlaying(true)

        setEvent(events.videoLessons.chapter_started, {
          lesson_name: lessonSelected?.name,
          lesson_id: lessonSelected?.lessonId,
          chapter_name: chapter?.name,
          chapter_id: chapterId
        })
      }, [setEvent, lessonSelected?.name, lessonSelected?.lessonId, chapter?.name, chapterId])

      const handlePause = useCallback(() => {
        setIsPlaying(false)
      }, [])

      const handleTimeUpdate = useCallback(() => {
        try {
          if (videoRef.current && chapterId) {
            setCurrentVideo(
              {
                currentPosition: videoRef.current.currentTime,
                isPlaying: !videoRef.current.paused
              },
              chapterId
            )
          }
        } catch (error) {
          console.error('Error in timeupdate handler:', error)
          if (videoRef.current) {
            // Remove the event listener if we encounter an error
            videoRef.current.removeEventListener('timeupdate', handleTimeUpdate)
          }
        }
      }, [chapterId, setCurrentVideo])

      const handleFullscreenChange = useCallback(() => {
        setIsFullScreen(!!document.fullscreenElement)
      }, [])

      // Initialize HTML5 video player with event listeners using AbortController
      const controller = new AbortController()
      const { signal } = controller

      useEffect(() => {
        // Set up event listeners with the abort signal
        if (videoRef.current) {
          videoRef.current.addEventListener('loadedmetadata', handleMetadata, { signal })
          videoRef.current.addEventListener('play', handlePlay, { signal })
          videoRef.current.addEventListener('pause', handlePause, { signal })
          videoRef.current.addEventListener('ended', onEnd, { signal })
          videoRef.current.addEventListener('timeupdate', handleTimeUpdate, { signal })
        }
        document.addEventListener('fullscreenchange', handleFullscreenChange, { signal })

        // Clean up all event listeners at once when component unmounts
        return () => {
          controller.abort()
        }
      }, [handleMetadata, handlePlay, handlePause, handleTimeUpdate, handleFullscreenChange, onEnd, controller, signal])

      // Handle fullscreen toggle
      const toggleFullscreen = () => {
        if (videoRef.current) {
          if (!document.fullscreenElement) {
            videoRef.current.requestFullscreen().catch((err) => {
              console.error(`Error attempting to enable fullscreen: ${err.message}`)
            })
          } else {
            document.exitFullscreen()
          }
        }
      }

      return (
        <DS.Frame>
          <DS.Frame style={styles.videoContainer}>
            <video
              ref={videoRef}
              style={styles.video}
              controls
              onDoubleClick={toggleFullscreen}
              src={chapter.videoUrl}
              crossOrigin="anonymous"
            >
              <track kind="captions" src={chapter.subtitlesUrl || ''} srcLang="en" label="English" />
            </video>
          </DS.Frame>
          {areSubsAvailable ? (
            <DS.Frame style={styles.subsControls}>
              <TouchableOpacity onPress={changeSubtitleStatus} style={styles.button}>
                <DS.Text variant="Body Small/Medium" color="Global/White" style={styles.buttonText}>
                  {textButton} CC
                </DS.Text>
              </TouchableOpacity>
            </DS.Frame>
          ) : null}
        </DS.Frame>
      )
    }
  )
)

const styles = StyleSheet.create({
  videoContainer: {
    height: 200
  },
  video: {
    width: '100%',
    height: '100%'
  },
  subsControls: {
    backgroundColor: 'transparent',
    borderRadius: 5,
    position: 'absolute',
    top: -9,
    left: 30,
    zIndex: 3
  },
  button: {
    paddingHorizontal: DS.spacing[8],
    paddingVertical: 10.5,
    borderRadius: DS.spacing[12],
    width: 90,
    height: 38,
    marginTop: 10
  },
  buttonText: {
    textAlign: 'center'
  }
})
